/**
 * Legal & AI-transparency copy — kept in a dedicated module so we can update
 * legal text without touching the main i18n dictionary.
 * Bulgarian is authoritative (project is Bulgaria-based); English is a mirror.
 */

const LAST_UPDATED = 'March 5, 2026';

export const legalStrings = {
  en: {
    lastUpdated: `Last updated: ${LAST_UPDATED}`,
    common: {
      privacy: 'Privacy Policy',
      terms: 'Terms of Service',
      backHome: 'Back to home',
    },
    privacy: {
      title: 'Privacy Policy',
      intro: 'AIgnyx ("we", "us") takes your privacy seriously. This policy explains what we collect, why, and the rights you have under the GDPR.',
      sections: [
        {
          heading: 'Data we collect',
          items: [
            'Account data: email, name, and (for password auth) a bcrypt-hashed password. For Google login we receive email, name, and profile picture.',
            'Usage data: which generator tools you use, how many generations you\'ve consumed, and your project history.',
            'Billing data: processed exclusively by Stripe. We store only a payment reference id, plan, amount, and status — never your card details.',
            'Email logs: subject, event type, and delivery status of transactional emails we send you.',
          ]
        },
        {
          heading: 'Why we collect it',
          items: [
            'To provide the service: authentication, saving your generated ads, enforcing plan limits.',
            'To bill you when you upgrade to a paid plan (legal basis: contract).',
            'To send transactional emails you\'d expect (generation complete, billing confirmation) — legal basis: legitimate interest.',
            'To send marketing/digest emails only if you explicitly opt in — legal basis: consent.',
          ]
        },
        {
          heading: 'Retention',
          items: [
            'Account and usage data are kept for as long as your account is active.',
            'Email logs are kept for 12 months for delivery debugging, then automatically purged.',
            'Billing records are kept for 7 years as required by tax law.',
            'If you delete your account (see below), everything except legally required billing records is erased immediately.',
          ]
        },
        {
          heading: 'Third-party processors',
          items: [
            'Stripe (payments) — https://stripe.com/privacy',
            'Resend (transactional email delivery) — https://resend.com/legal/privacy-policy',
            'OpenAI (via Emergent LLM Key) — receives your ad-generation prompts to produce output; no personal data is intentionally sent.',
            'Our hosting provider — infrastructure only, no access to application data.',
          ]
        },
        {
          heading: 'Your rights',
          items: [
            'Access — request a copy of the data we hold about you.',
            'Rectification — correct anything that\'s wrong (mostly editable from Settings).',
            'Erasure — delete your account and all associated data from Settings → Delete Account.',
            'Portability — request an export of your generated content.',
            'Objection — opt out of marketing emails at any time in Settings.',
            'Complaint — you may complain to the Bulgarian Data Protection Commission (kzld.bg) or your local supervisory authority.',
          ]
        },
        {
          heading: 'Contact',
          items: [
            'For any privacy question or right-exercise request, email us at privacy@adforge.ai.',
          ]
        }
      ]
    },
    terms: {
      title: 'Terms of Service',
      intro: 'By using AIgnyx you agree to the terms below. These are meant to be readable — no dark patterns, no hidden clauses.',
      sections: [
        {
          heading: 'The service',
          items: [
            'AIgnyx is a SaaS tool that uses AI models to generate short-form ad content (hooks, scripts, storyboards, variations, etc.). We are not affiliated with TikTok, Instagram, YouTube, Pika, Kling, or Runway.',
            'The generated content is produced by artificial intelligence and may contain errors, biases, or unexpected outputs. You are responsible for reviewing before publishing.',
          ]
        },
        {
          heading: 'Your account',
          items: [
            'You must be 16 or older to sign up.',
            'You are responsible for keeping your login credentials safe.',
            'One person or business per account — sharing an account across multiple advertisers is not allowed on Starter or Pro plans.',
          ]
        },
        {
          heading: 'Acceptable use',
          items: [
            'Do not use AIgnyx to generate content that is illegal, defamatory, harassing, hateful, sexually explicit, or that impersonates a real person without permission.',
            'Do not use it to promote products or services that are illegal where the ad will run.',
            'We may suspend accounts that abuse the service or attempt to reverse-engineer/scrape it.',
          ]
        },
        {
          heading: 'Ownership of generated content',
          items: [
            'Subject to these terms, you own the ad content you generate and may use it commercially.',
            'AI models don\'t guarantee originality — we can\'t promise the same phrase won\'t come out of another user\'s prompt. If a piece of output looks like it copies someone else\'s trademark, don\'t use it.',
          ]
        },
        {
          heading: 'Limitation of liability',
          items: [
            'AIgnyx does not guarantee any specific business outcome — sales, conversions, engagement, or reach — from the ad content it generates. AI models produce variations, not promises.',
            'You are solely responsible for reviewing, editing, and publishing the generated content before using it publicly or in paid campaigns.',
            'We are not liable for losses arising from use of generated content without your prior review — including ad platform bans, moderator rejections, reputational harm, or lost profits.',
            'Our maximum aggregate liability to you under any circumstances is limited to the amount you paid us in the 12 months preceding the claim.',
          ]
        },
        {
          heading: 'Service availability',
          items: [
            'We make reasonable efforts to keep the platform continuously available but do not guarantee any specific uptime percentage.',
            'Planned maintenance will be announced in advance where possible — usually by email or in-app notice.',
            'For unexpected outages (infrastructure provider issues, third-party AI provider problems) we do everything reasonable to restore service as quickly as possible.',
          ]
        },
        {
          heading: 'Cancellation and refund policy',
          items: [
            'As an EU consumer you have a standard 14-day right of withdrawal from digital services under Directive 2011/83/EU.',
            'IMPORTANT: By explicitly consenting at first payment (the checkbox "I understand I lose my right of withdrawal once I start using the service") you waive this 14-day right per Article 16(m) of the Directive, because the service begins delivering content immediately.',
            'You may cancel your subscription at any time from Settings → Billing. Cancellation takes effect at the end of the current paid period — no pro-rated refunds are issued unless required by law.',
            'For mistaken or disputed charges, email billing@adforge.ai — we refund within 14 days when the claim is valid.',
          ]
        },
        {
          heading: 'AI-generated content',
          items: [
            'Everything the platform produces is generated by an artificial intelligence system — as disclosed during signup and labeled above every generator.',
            'By accepting these terms you confirm you understand this and that any use of the output is at your own responsibility.',
            'Our full AI transparency commitments under EU AI Act Article 50 are set out in the Privacy Policy and the signup page.',
          ]
        },
        {
          heading: 'Changes to these terms',
          items: [
            'If we change the terms materially, we\'ll notify you by email at least 14 days in advance.',
          ]
        },
        {
          heading: 'Governing law',
          items: [
            'These terms are governed by the laws of the Republic of Bulgaria. Disputes go to the competent court in Sofia.',
          ]
        }
      ]
    },
    aiDisclosure: {
      short: 'AI-generated content',
      long: 'This content is generated by an artificial intelligence system. Review before use.',
      signupNotice: 'AIgnyx uses artificial intelligence to generate ad content. By creating an account you acknowledge you\'re interacting with an AI system that may produce imperfect or unexpected results.',
      signupAcknowledge: 'I understand that AIgnyx is an AI-powered tool and outputs may need review.',
      legalAcknowledge: 'You must agree to the Privacy Policy and Cookie Policy.',
      legalAcknowledgePrefix: 'I have read and agree to the',
      legalPrivacyLabel: 'Privacy Policy',
      legalCookieLabel: 'Cookie Policy',
      legalAndWord: 'and',
      marketingOptIn: 'I want to receive occasional tips, weekly digests, and product updates (optional — you can change this anytime in Settings).'
    },
    cookies: {
      title: 'Cookies',
      body: 'AIgnyx uses a strictly-necessary cookie to keep you logged in. We do not use tracking or advertising cookies. You can accept or reject non-essential cookies below.',
      accept: 'Accept all',
      reject: 'Reject non-essential',
      learnMore: 'Cookie Policy'
    },
    settings: {
      preferencesTitle: 'Email preferences',
      marketingConsentLabel: 'Weekly digest & marketing emails',
      marketingConsentHelp: 'Occasional tips, product updates, and viral-potential ad trends. Separate from transactional emails (generation complete, billing).',
      dangerTitle: 'Danger zone',
      deleteAccountLabel: 'Delete my account and all data',
      deleteAccountHelp: 'This permanently removes your account, projects, and all associated data. Cannot be undone.',
      deleteAccountBtn: 'Delete my account',
      deleteConfirmTitle: 'Are you absolutely sure?',
      deleteConfirmBody: 'This will permanently delete your account, all projects, generations, and any personal data we hold. Billing records required by tax law will be retained for 7 years. This action cannot be undone.',
      deleteConfirmType: 'Type DELETE to confirm',
      deleteConfirmBtn: 'Permanently delete my account',
      deleteCancel: 'Cancel',
      preferencesUpdated: 'Preferences saved',
      accountDeleted: 'Your account has been deleted',
      dataExportTitle: 'Download my data',
      dataExportLabel: 'Export all my data (GDPR Article 20)',
      dataExportHelp: 'Get a copy of everything we hold: account details, projects, generation history, and billing records. Delivered to your email as a downloadable zip.',
      dataExportBtn: 'Download my data'
    },
    checkout: {
      title: 'Confirm your subscription',
      selectedPlanLabel: 'Selected plan',
      waiverTitle: 'Right of withdrawal — required by EU law',
      waiverBody: 'Because the service delivers AI-generated content immediately after payment, EU law (Directive 2011/83/EU, Article 16(m)) requires your explicit acknowledgement that you will lose your 14-day right of withdrawal once you start using it.',
      waiverCheckbox: 'I explicitly agree that the service starts immediately and I understand I lose my 14-day right of withdrawal once I generate content.',
      termsAcceptLabel: 'I have read and agree to the Terms of Service and Refund Policy.',
      readTerms: 'Read the Terms',
      continueBtn: 'Continue to secure checkout',
      cancelBtn: 'Cancel'
    }
  },
  bg: {
    lastUpdated: `Последна редакция: 5 март 2026 г.`,
    common: {
      privacy: 'Политика за поверителност',
      terms: 'Общи условия',
      backHome: 'Обратно към началото',
    },
    privacy: {
      title: 'Политика за поверителност',
      intro: 'AIgnyx („ние") се отнася сериозно с личните ти данни. Тази политика обяснява какво събираме, защо и какви права имаш съгласно GDPR.',
      sections: [
        {
          heading: 'Какви данни събираме',
          items: [
            'Данни за акаунта: имейл, име и (при вход с парола) bcrypt-хеширана парола. При Google вход получаваме имейл, име и профилна снимка.',
            'Данни за използване: кои генератори използваш, колко генерации си направил и историята на проектите ти.',
            'Платежни данни: обработват се изцяло от Stripe. Ние съхраняваме само reference id, план, сума и статус — никога данни на картата ти.',
            'Имейл логове: тема, тип събитие и статус на транзакционните имейли, които ти изпращаме.',
          ]
        },
        {
          heading: 'Защо ги събираме',
          items: [
            'За да предоставим услугата: удостоверяване, запазване на генерациите, следене на лимитите на плана.',
            'За да те таксуваме при ъпгрейд към платен план (правно основание: договор).',
            'За да ти изпращаме транзакционни имейли (готова генерация, потвърждение за плащане) — правно основание: легитимен интерес.',
            'За да изпращаме маркетингови/седмични имейли само ако си дал изрично съгласие — правно основание: съгласие.',
          ]
        },
        {
          heading: 'Срок на съхранение',
          items: [
            'Данните за акаунта и използването се пазят докато акаунтът е активен.',
            'Имейл логовете се пазят 12 месеца за отстраняване на проблеми с доставка, после автоматично се изтриват.',
            'Счетоводните записи се пазят 7 години, както изисква данъчното законодателство.',
            'При изтриване на акаунт (виж по-долу) всичко освен законово изискваните записи се изтрива веднага.',
          ]
        },
        {
          heading: 'Обработващи лица',
          items: [
            'Stripe (плащания) — https://stripe.com/privacy',
            'Resend (доставка на транзакционни имейли) — https://resend.com/legal/privacy-policy',
            'OpenAI (чрез Emergent LLM Key) — получава промптите ти за генериране; лични данни не се изпращат целенасочено.',
            'Нашият хостинг доставчик — само инфраструктура, без достъп до данни на приложението.',
          ]
        },
        {
          heading: 'Твоите права',
          items: [
            'Достъп — да поискаш копие на данните за теб.',
            'Коригиране — да оправиш нещо грешно (повечето неща са достъпни от Настройки).',
            'Изтриване — да изтриеш акаунта си и всички свързани данни от Настройки → Изтрий акаунт.',
            'Преносимост — да поискаш експорт на генерациите си.',
            'Възражение — да се откажеш от маркетингови имейли по всяко време от Настройки.',
            'Жалба — можеш да подадеш жалба до Комисията за защита на личните данни (kzld.bg).',
          ]
        },
        {
          heading: 'Контакт',
          items: [
            'За всеки въпрос по поверителност или упражняване на права: privacy@adforge.ai.',
          ]
        }
      ]
    },
    terms: {
      title: 'Общи условия',
      intro: 'Като използваш AIgnyx, приемаш условията по-долу. Целим се в четимост — без скрити клаузи, без dark patterns.',
      sections: [
        {
          heading: 'Услугата',
          items: [
            'AIgnyx е SaaS инструмент, който използва AI модели за генериране на кратко рекламно съдържание (hooks, скриптове, storyboards, вариации и др.). Не сме свързани с TikTok, Instagram, YouTube, Pika, Kling или Runway.',
            'Генерираното съдържание се произвежда от изкуствен интелект и може да съдържа грешки, пристрастия или неочаквани резултати. Ти носиш отговорност за преглед преди публикуване.',
          ]
        },
        {
          heading: 'Твоят акаунт',
          items: [
            'Трябва да си на 16 или повече години, за да се регистрираш.',
            'Отговорен си за сигурността на входните си данни.',
            'Един акаунт на човек/бизнес — споделяне между няколко рекламодатели не е позволено при плановете Starter и Pro.',
          ]
        },
        {
          heading: 'Приемлива употреба',
          items: [
            'Не използвай AIgnyx за съдържание, което е незаконно, клеветническо, тормозещо, разпалващо омраза, сексуално експлицитно или се представя за друг човек без разрешение.',
            'Не рекламирай продукти или услуги, които са незаконни там, където ще излезе рекламата.',
            'Може да блокираме акаунти, които злоупотребяват или се опитват да реверс-инженерят/скрейпват услугата.',
          ]
        },
        {
          heading: 'Собственост на генерираното съдържание',
          items: [
            'При спазване на тези условия, ти си собственик на генерираните реклами и можеш да ги използваш комерсиално.',
            'AI моделите не гарантират оригиналност — не можем да обещаем, че същата фраза няма да излезе и от чужд промпт. Ако някой резултат прилича на нечия чужда търговска марка, не го използвай.',
          ]
        },
        {
          heading: 'Ограничение на отговорността',
          items: [
            'AIgnyx не гарантира конкретни бизнес резултати — продажби, конверсии, ангажираност или обхват — от генерираното рекламно съдържание. AI моделите произвеждат вариации, не обещания.',
            'Ти носиш пълна отговорност за прегледа, редакцията и публикуването на генерираното съдържание преди да го използваш публично или в платени кампании.',
            'Не носим отговорност за загуби, произтичащи от използване на генерирано съдържание без предварителен преглед — включително банове от рекламни платформи, отхвърляне от модератори, репутационни щети или пропуснати ползи.',
            'Максималната ни съвкупна отговорност към теб при всякакви обстоятелства е ограничена до сумата, която си ни платил в предходните 12 месеца.',
          ]
        },
        {
          heading: 'Наличност на услугата',
          items: [
            'Полагаме разумни усилия да поддържаме платформата достъпна непрекъснато, но не гарантираме конкретен процент uptime.',
            'Планирана поддръжка ще бъде обявявана предварително, когато е възможно — обикновено по имейл или чрез съобщение в приложението.',
            'При извънредни прекъсвания (проблеми с доставчик на инфраструктура, външен AI провайдер) правим всичко разумно, за да възстановим услугата възможно най-бързо.',
          ]
        },
        {
          heading: 'Политика за отказ и възстановяване',
          items: [
            'Като потребител в ЕС имаш стандартно 14-дневно право на отказ от дигитални услуги съгласно Директива 2011/83/ЕС.',
            'ВАЖНО: С изричното си съгласие при първо плащане (чекбокс „Разбирам, че губя правото на отказ след започване на ползването") ти губиш това 14-дневно право съгласно чл. 16, буква „м" от Директивата, тъй като услугата стартира незабавно с генериране на съдържание.',
            'Анулиране на абонамент е възможно по всяко време от Настройки → Плащания. Отказът влиза в сила в края на текущия платен период — не се възстановяват пропорционални суми, освен ако законът не изисква обратното.',
            'При погрешна или спорна такса ни пиши на billing@adforge.ai — възстановяваме до 14 дни, когато е основателно.',
          ]
        },
        {
          heading: 'AI-генерирано съдържание',
          items: [
            'Всичко, генерирано от платформата, е произведено от система с изкуствен интелект — както е обяснено при регистрацията ти и както е обозначено с етикет над всеки генератор.',
            'Задължавайки се към тези условия, потвърждаваш, че си запознат с това и че всяка употреба на резултатите е на твоя отговорност.',
            'Пълните ни AI transparency ангажименти по член 50 от Регламента за ИИ на ЕС са изложени в Политиката за поверителност и на страницата за регистрация.',
          ]
        },
        {
          heading: 'Промени в условията',
          items: [
            'При съществена промяна те уведомяваме по имейл поне 14 дни предварително.',
          ]
        },
        {
          heading: 'Приложимо право',
          items: [
            'Тези условия се уреждат от законите на Република България. Споровете отиват пред компетентния съд в София.',
          ]
        }
      ]
    },
    aiDisclosure: {
      short: 'Съдържание, генерирано от AI',
      long: 'Това съдържание е генерирано от система с изкуствен интелект. Прегледай преди употреба.',
      signupNotice: 'AIgnyx използва изкуствен интелект за генериране на рекламно съдържание. Създавайки акаунт, потвърждаваш, че взаимодействаш с AI система, която може да произведе неточни или неочаквани резултати.',
      signupAcknowledge: 'Разбирам, че AIgnyx е базиран на изкуствен интелект и резултатите може да изискват преглед.',
      legalAcknowledge: 'Трябва да се съгласиш с Политиката за поверителност и Политиката за бисквитки.',
      legalAcknowledgePrefix: 'Прочетох и приемам',
      legalPrivacyLabel: 'Политиката за поверителност',
      legalCookieLabel: 'Политиката за бисквитки',
      legalAndWord: 'и',
      marketingOptIn: 'Искам да получавам съвети, седмични обзори и продуктови новини (незадължително — можеш да промениш това по всяко време от Настройки).'
    },
    cookies: {
      title: 'Бисквитки',
      body: 'AIgnyx използва строго необходима бисквитка, за да те държи в системата. Не използваме проследяващи или рекламни бисквитки. Можеш да приемеш или откажеш неесенциалните по-долу.',
      accept: 'Приеми всички',
      reject: 'Отхвърли неесенциалните',
      learnMore: 'Политика за бисквитки'
    },
    settings: {
      preferencesTitle: 'Имейл предпочитания',
      marketingConsentLabel: 'Седмичен обзор и маркетингови имейли',
      marketingConsentHelp: 'От време на време съвети, продуктови новини и тенденции за реклами с вирусен потенциал. Отделно от транзакционните имейли (готова генерация, плащания).',
      dangerTitle: 'Опасна зона',
      deleteAccountLabel: 'Изтрий акаунта и всички данни',
      deleteAccountHelp: 'Това перманентно премахва акаунта, проектите и всички свързани данни. Не може да се върне обратно.',
      deleteAccountBtn: 'Изтрий акаунта ми',
      deleteConfirmTitle: 'Абсолютно ли си сигурен?',
      deleteConfirmBody: 'Това ще изтрие завинаги акаунта ти, всички проекти, генерации и лични данни, които държим. Счетоводните записи, изисквани от закона, ще бъдат запазени 7 години. Действието е необратимо.',
      deleteConfirmType: 'Напиши DELETE, за да потвърдиш',
      deleteConfirmBtn: 'Перманентно изтрий акаунта ми',
      deleteCancel: 'Отказ',
      preferencesUpdated: 'Предпочитанията са запазени',
      accountDeleted: 'Акаунтът ти беше изтрит',
      dataExportTitle: 'Изтегли моите данни',
      dataExportLabel: 'Експортирай всички мои данни (GDPR член 20)',
      dataExportHelp: 'Получи копие на всичко, което пазим: данни за акаунта, проекти, история на генерации и записи за плащания. Изпраща се на имейла ти като изтегляем архив.',
      dataExportBtn: 'Изтегли моите данни'
    },
    checkout: {
      title: 'Потвърди абонамента',
      selectedPlanLabel: 'Избран план',
      waiverTitle: 'Право на отказ — изискване на ЕС',
      waiverBody: 'Тъй като услугата стартира с генериране на AI съдържание веднага след плащане, законодателството на ЕС (Директива 2011/83/ЕС, чл. 16, буква „м") изисква изричното ти потвърждение, че губиш 14-дневното право на отказ след като започнеш да я ползваш.',
      waiverCheckbox: 'Изрично се съгласявам, че услугата стартира незабавно и разбирам, че губя правото си на 14-дневен отказ след като генерирам съдържание.',
      termsAcceptLabel: 'Прочетох и приемам Общите условия и Политиката за възстановяване.',
      readTerms: 'Прочети условията',
      continueBtn: 'Продължи към сигурно плащане',
      cancelBtn: 'Отказ'
    }
  }
};

// Spanish/German fall back to English legal text so we always render something
// (a native translation for those markets is a follow-up task).
legalStrings.es = legalStrings.en;
legalStrings.de = legalStrings.en;

export function useLegal(language) {
  return legalStrings[language] || legalStrings.en;
}
